export function cartesianProduct(arr: (string | string[])[]): string[][] {
  // 辅助函数：计算笛卡尔积
  const computeProduct = (lists: string[][]): string[][] => {
    if (lists.length === 0) return [[]];
    const [first, ...rest] = lists;
    const remainingProduct = computeProduct(rest);
    return first.flatMap(f => remainingProduct.map(r => [f, ...r]));
  };

  // 将输入数组扁平化为二维数组
  const normalizedArray = arr.map(item => {
    if (Array.isArray(item)) {
      return item;
    }

    if (item.startsWith('%') && item.endsWith('%')) {
      const value = item.substring(1, item.length - 1);
      return [value];
    }

    return [item];
  });

  // 计算笛卡尔积并将其转换为字符串数组
  const result = computeProduct(normalizedArray);

  return result;
}

export function parseRuleByLine(text: string) {
  // 边界判断：检查输入是否有效
  if (!text || typeof text !== 'string') {
    return [];
  }

  // 去除首尾空白字符
  text = text.trim();
  if (text === '') {
    return [];
  }

  // 规则语法说明：
  // - + : AND 关系(必须同时包含)
  // - ; : OR 关系(任选其一)
  // - , : OR 关系(任选其一，与 ; 功能相同)
  // - %text% : 精确匹配(包括空格和标点)
  // - 括号 : 分组,支持嵌套组合

  // 首先处理复杂的括号 + 百分号模式
  // 修正：只处理真正复杂的情况，如 (%Critical + High%;Medium)，而不是 (% PC %;% PC, %)
  const complexPattern = /\((%[^%]*%[^;]*;[^%]*%[^%]*\))/g;
  let processedText = text;
  const complexReplacements: { index: number; value: string[] }[] = [];

  // 处理 (%Critical + High%;Medium) 这种模式
  let match;
  while ((match = complexPattern.exec(processedText)) !== null) {
    // const fullMatch = match[0];
    const innerContent = match[1];

    // 提取 %% 内容和分号后面的内容
    const percentMatch = innerContent.match(/%([^%]*)%/);
    if (percentMatch) {
      const percentContent = percentMatch[1];
      const afterPercent = innerContent
        .substring(percentMatch[0].length)
        .trim();

      if (afterPercent.startsWith(';')) {
        const secondOption = afterPercent.substring(1).trim();
        complexReplacements.push({
          index: match.index,
          value: [percentContent, secondOption],
        });
      }
    }
  }

  // 替换复杂模式
  complexReplacements.reverse().forEach((replacement, idx) => {
    const placeholder = `__COMPLEX_${idx}__`;
    const start = replacement.index;
    // 找到完整的括号匹配
    let bracketCount = 0;
    let end = start;
    for (let i = start; i < processedText.length; i++) {
      if (processedText[i] === '(') {
        bracketCount = bracketCount + 1;
      } else if (processedText[i] === ')') {
        bracketCount = bracketCount - 1;
        if (bracketCount === 0) {
          end = i + 1;
          break;
        }
      }
    }
    processedText =
      processedText.substring(0, start) +
      placeholder +
      processedText.substring(end);
  });

  // 处理简单的 %% 模式
  const simplePercentPattern = /%[^%]*%/g;
  const simpleReplacements: {
    index: number;
    value: string;
    originalLength: number;
  }[] = [];

  while ((match = simplePercentPattern.exec(processedText)) !== null) {
    const content = match[0].slice(1, -1);
    simpleReplacements.push({
      index: match.index,
      value: content,
      originalLength: match[0].length,
    });
  }

  // 替换简单 %% 模式 - 从后往前替换，但保持原始索引
  simpleReplacements
    .slice()
    .reverse()
    .forEach(replacement => {
      const originalIdx = simpleReplacements.indexOf(replacement);
      const placeholder = `__SIMPLE_${originalIdx}__`;
      const start = replacement.index;
      const end = start + replacement.originalLength;
      processedText =
        processedText.substring(0, start) +
        placeholder +
        processedText.substring(end);
    });

  // 按 + 分割
  const parts = processedText
    .split('+')
    .map(part => part.trim())
    .filter(part => Boolean(part));
  const resultList: (string | string[])[] = [];

  parts.forEach(part => {
    // 检查是否是复杂占位符
    const complexMatch = part.match(/^__COMPLEX_(\d+)__$/);
    if (complexMatch) {
      const idx = parseInt(complexMatch[1]);
      const replacement = complexReplacements.find(
        r => complexReplacements.indexOf(r) === idx,
      );
      if (replacement) {
        resultList.push(replacement.value);
      }
      return;
    }

    // 检查是否是简单占位符
    const simpleMatch = part.match(/^__SIMPLE_(\d+)__$/);
    if (simpleMatch) {
      const idx = parseInt(simpleMatch[1]);
      const replacement = simpleReplacements.find(
        r => simpleReplacements.indexOf(r) === idx,
      );
      if (replacement) {
        resultList.push(replacement.value);
      }
      return;
    }

    // 处理普通括号内的分号或逗号
    if (part.includes(';') || part.includes(',')) {
      let cleanPart = part;
      if (cleanPart.startsWith('(') && cleanPart.endsWith(')')) {
        cleanPart = cleanPart.substring(1, cleanPart.length - 1);
      }

      const options = cleanPart
        .split(/[;,]/)
        .map(opt => {
          // 检查原始选项是否包含占位符
          const hasPlaceholder = /__(SIMPLE|COMPLEX)_\d+__/.test(opt);

          // 先进行占位符替换
          let replacedOpt = opt;
          simpleReplacements.forEach((replacement, idx) => {
            const placeholder = `__SIMPLE_${idx}__`;
            replacedOpt = replacedOpt.replace(placeholder, replacement.value);
          });

          complexReplacements.forEach((replacement, idx) => {
            const placeholder = `__COMPLEX_${idx}__`;
            replacedOpt = replacedOpt.replace(
              placeholder,
              replacement.value as string,
            );
          });

          // 对于从占位符替换得到的内容，不去除前后空格
          const trimmedOpt = hasPlaceholder ? replacedOpt : replacedOpt.trim();
          return trimmedOpt;
        })
        .filter(opt => Boolean(opt));
      resultList.push(options);
    } else {
      // 检查是否包含未处理的占位符，如果有则直接替换
      let finalPart = part;
      simpleReplacements.forEach((replacement, idx) => {
        const placeholder = `__SIMPLE_${idx}__`;
        finalPart = finalPart.replace(placeholder, replacement.value);
      });

      complexReplacements.forEach((replacement, idx) => {
        const placeholder = `__COMPLEX_${idx}__`;
        finalPart = finalPart.replace(placeholder, replacement.value as string);
      });

      resultList.push(finalPart);
    }
  });

  return cartesianProduct(resultList);
}

export function parseRule(text: string) {
  // 边界判断：检查输入是否有效
  if (!text || typeof text !== 'string') {
    return [];
  }

  const list = text.split('\n').filter(item => item.trim() !== '');
  let result: string[][] = [];

  if (list.length === 0) {
    return [];
  }

  try {
    list.forEach(item => {
      const list = parseRuleByLine(item);
      result = result.concat(list);
    });
  } catch (error) {
    console.error('Error parsing rule:', error);
    return [];
  }

  return result;
}

/**
 * 验证给定的规则字符串中的括号和加号的合法性。
 *
 * 该函数检查字符串中的括号是否匹配，并确保在括号外的部分只能包含加号。
 *
 * @param rule - 需要验证的规则字符串。
 * @returns 如果规则字符串合法返回 true，否则返回 false。
 */
export function isValidExpression(expression: string): boolean {
  function validateString(s: string): boolean {
    // 检查括号闭合
    const stack: string[] = [];
    for (const char of s) {
      if (char === '(') {
        stack.push(char);
      } else if (char === ')') {
        if (stack.length === 0 || stack[stack.length - 1] !== '(') {
          return false;
        }
        stack.pop();
      }
    }
    if (stack.length !== 0) return false;

    // 检查括号块之间是否有加号连接
    const pattern = /\)[^+]*\(/;
    if (pattern.test(s)) {
      return false;
    }

    return true;
  }

  return validateString(expression);
}

export const keywordsTipsConfig = [
  {
    title: 'Bug',
    desc: 'Content must mention exactly “{text}”',
    params: { text: 'Bug' },
  },
  {
    title: 'Bug ; Issue | Bug , Issue',
    desc: 'Content must mention either “{text}” or exactly “{text1}”',
    params: { text: 'Bug', text1: 'Issue' },
  },
  {
    title: 'Bug + Issue',
    desc: 'Content must mention both “{text}” and “{text1}”',
    params: { text: 'Bug', text1: 'Issue' },
  },
  {
    title: 'Bug + (Issue ; Problem)',
    desc: 'Content must mention “{text}” or “{text1}”',
    params: { text: 'Bug + Issue', text1: 'Bug + Problem' },
  },
  {
    title: '% Bug, %',
    desc: 'Content must contain exactly "{text}", including spaces and punctuation.',
    params: { text: ' Bug, ' },
  },
];

export const keywordsReplaceText = [
  { text: '（', value: '(' },
  { text: '）', value: ')' },
  { text: '；', value: ';' },
  { text: '，', value: ',' },
];
